import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Radio, Play, Pause, Loader2, ChevronDown, Music, Music2 } from "lucide-react";
import { postNarrate } from "@/lib/api";
import { pickAmbientTrack } from "@/lib/ambient-tracks";
import { trackEvent } from "@/lib/analytics";

const VOICES = [
  { key: "onyx", label: "Onyx — Deep announcer" },
  { key: "fable", label: "Fable — Storyteller" },
  { key: "echo", label: "Echo — Calm host" },
  { key: "nova", label: "Nova — Upbeat DJ" },
  { key: "shimmer", label: "Shimmer — Bright" },
  { key: "sage", label: "Sage — Measured" },
];

export default function RadioBulletin({ snapshotId, meta, t }) {
  const [voice, setVoice] = useState("onyx");
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [musicOn, setMusicOn] = useState(true);
  const audioRef = useRef(null);
  const musicRef = useRef(null);
  const prewarmRef = useRef(false);

  // Pick era-appropriate ambient bed based on the snapshot's year
  const ambientTrack = useMemo(() => {
    const y = parseInt((meta?.date || "").slice(0, 4), 10) || 1970;
    return pickAmbientTrack(y);
  }, [meta?.date]);

  // Music volume mirrors the announcer: gentle when voice is speaking (duck to 0.03),
  // slightly louder in the intro/outro silence (0.12). Full silence when narration off.
  useEffect(() => {
    const el = musicRef.current;
    if (!el) return;
    if (!musicOn || !playing) {
      el.pause();
      return;
    }
    el.volume = 0.12;
    if (el.paused) el.play().catch(() => { /* autoplay blocked */ });
  }, [musicOn, playing]);

  // Duck music by monitoring the voice audio's currentTime. We can't hook into
  // Web Audio easily for a data-URL <audio>, so we use a lightweight interval
  // — cheap and reliable for background music envelopes.
  useEffect(() => {
    if (!musicOn || !playing) return;
    const iv = setInterval(() => {
      const voiceEl = audioRef.current;
      const musicEl = musicRef.current;
      if (!voiceEl || !musicEl) return;
      // If voice audio is playing, duck. Otherwise let ambient breathe.
      const voiceActive = voiceEl.currentTime > 0.1 && !voiceEl.paused && !voiceEl.ended;
      musicEl.volume = voiceActive ? 0.03 : 0.12;
    }, 400);
    return () => clearInterval(iv);
  }, [musicOn, playing]);

  // Prewarm the bulletin audio in the background as soon as the component
  // mounts. Bulletin mode is unmetered (free for everyone), so kicking off
  // the ~10-15s TTS generation immediately means the audio is usually ready
  // by the time the user clicks Play.
  useEffect(() => {
    if (!snapshotId || prewarmRef.current) return;
    prewarmRef.current = true;
    (async () => {
      try {
        const data = await postNarrate(snapshotId, voice);
        const src = `data:${data.mime || "audio/mpeg"};base64,${data.audio_base64}`;
        setAudioUrl(src);
        setReady(true);
        // Also imperatively set + preload the audio element so a subsequent
        // click on Play plays instantly with no gesture-to-src race.
        if (audioRef.current) {
          audioRef.current.src = src;
          try { audioRef.current.load(); } catch { /* older Safari */ }
        }
      } catch {
        // Silent — user can still click Play to try again
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotId]);

  const generate = async (v) => {
    setLoading(true);
    setPlaying(false);
    trackEvent("bulletin_play", { snapshot_id: snapshotId, voice: v || voice });
    try {
      const data = await postNarrate(snapshotId, v || voice);
      const src = `data:${data.mime || "audio/mpeg"};base64,${data.audio_base64}`;
      setAudioUrl(src);
      if (audioRef.current) {
        audioRef.current.src = src;
        try { audioRef.current.load(); } catch { /* noop */ }
        audioRef.current.play().then(() => setPlaying(true)).catch(() => {
          // Autoplay blocked — the src is set, next click will work
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate bulletin");
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = () => {
    const el = audioRef.current;
    // If audio isn't loaded yet OR the element's src is missing/stale
    // (browser navigation, voice-change), kick off a full generate.
    if (!audioUrl || !el || !el.src || el.src === window.location.href) {
      generate();
      return;
    }
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => {
        // Some iPadOS Safari cases lose the blob src on backgrounded tabs —
        // fall through to a fresh generate as a safety net.
        generate();
      });
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const pickVoice = (v) => {
    setVoice(v);
    setVoiceOpen(false);
    if (audioUrl) {
      setAudioUrl(null); // force regenerate for new voice
    }
  };

  const currentVoiceLabel = VOICES.find((x) => x.key === voice)?.label || voice;

  return (
    <section
      className="mb-6 border-2 border-ink bg-ink text-paper flex flex-col sm:flex-row items-stretch gap-0 tc-fade-up shadow-hard"
      data-testid="radio-bulletin"
    >
      <div className="flex items-center gap-3 p-4 sm:p-5 border-b-2 sm:border-b-0 sm:border-r-2 border-paper/30 min-w-[220px]">
        <div className="border-2 border-paper/60 p-2">
          <Radio size={22} />
        </div>
        <div>
          <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-paper/70">{t?.onAir || "On Air"}</div>
          <div className="font-headline text-xl font-bold leading-tight">{t?.radioBulletin || "Radio Bulletin"}</div>
        </div>
      </div>

      <div className="flex-1 p-4 sm:p-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 border-b-2 sm:border-b-0 sm:border-r-2 border-paper/30">
        <button
          data-testid="bulletin-play-btn"
          onClick={togglePlay}
          disabled={loading || (!ready && !audioUrl)}
          className="inline-flex items-center gap-2 bg-press-red text-paper font-mono uppercase tracking-widest text-xs px-4 py-3 border-2 border-paper hover:bg-press-redHover disabled:opacity-70"
        >
          {(loading || (!ready && !audioUrl)) ? <Loader2 size={16} className="animate-spin" /> : playing ? <Pause size={16} /> : <Play size={16} />}
          {loading
            ? (t?.rolling || "Rolling…")
            : (!ready && !audioUrl)
              ? (t?.warming || "Warming voice…")
              : audioUrl
                ? (playing ? (t?.pause || "Pause") : (t?.resume || "Resume"))
                : (t?.playBulletin || "Play Bulletin")}
        </button>
        <div className="flex-1 min-w-0">
          <audio
            ref={audioRef}
            data-testid="bulletin-audio"
            src={audioUrl || undefined}
            preload="auto"
            onEnded={() => setPlaying(false)}
            onPause={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            controls={!!audioUrl}
            style={{ width: "100%", filter: "invert(1)", opacity: audioUrl ? 1 : 0, transition: "opacity 300ms" }}
          />
          {!audioUrl && !loading && (
            <div className="font-mono text-[11px] uppercase tracking-widest text-paper/60">
              {meta?.long_date || ""} · {t?.bulletinHint || "A 20-second read of the top headlines & the day's #1 song"}
            </div>
          )}
        </div>
      </div>

      <div className="p-4 sm:p-5 relative min-w-[220px] flex items-center gap-2">
        <button
          onClick={() => {
            setMusicOn((v) => !v);
            trackEvent("bulletin_music_toggle", { snapshot_id: snapshotId, on: !musicOn });
          }}
          className="inline-flex items-center justify-center border-2 border-paper/60 p-2 hover:bg-paper/10"
          data-testid="bulletin-music-toggle"
          title={musicOn ? `Ambient music on — ${ambientTrack.name}` : "Ambient music off"}
          aria-label="Toggle background music"
        >
          {musicOn ? <Music size={14} /> : <Music2 size={14} className="opacity-40" />}
        </button>
        <div className="flex-1 relative">
        <button
          onClick={() => setVoiceOpen((v) => !v)}
          className="inline-flex items-center gap-2 border-2 border-paper/60 px-3 py-2 font-mono text-[11px] uppercase tracking-widest w-full justify-between hover:bg-paper/10"
          data-testid="voice-select-btn"
        >
          <span className="truncate">{currentVoiceLabel}</span>
          <ChevronDown size={14} />
        </button>
        {voiceOpen && (
          <ul className="absolute right-0 mt-1 z-40 bg-ink border-2 border-paper/60 min-w-[220px]" data-testid="voice-menu">
            {VOICES.map((v) => (
              <li key={v.key}>
                <button
                  onClick={() => pickVoice(v.key)}
                  data-testid={`voice-${v.key}`}
                  className={`w-full text-left px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-paper/10 ${
                    v.key === voice ? "text-press-red" : ""
                  }`}
                >
                  {v.label}
                </button>
              </li>
            ))}
          </ul>
        )}
        </div>
      </div>
      {/* Hidden ambient music bed — era-appropriate track that plays under the bulletin */}
      <audio
        ref={musicRef}
        data-testid="bulletin-music"
        src={ambientTrack.file}
        loop
        preload="auto"
        className="hidden"
      />
    </section>
  );
}
